import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';

import _ from 'lodash';

import {
  buildFieldPathToFieldTypeMap,
  buildFieldPathToFormKeyMap,
  coerceBroadcastValue,
} from 'utils/fieldPathMapping.utils';

import {
  selectAllRemoteFieldValues,
  selectRemoteFieldValuesClearReason,
} from 'store/presence/selectors';

const useRemoteFieldValueSync = ({
  config,
  isNestedForm = false,
  setForm,
  setInitialFormValues,
}) => {
  const remoteFieldValues = useSelector(selectAllRemoteFieldValues);
  const clearReason = useSelector(selectRemoteFieldValuesClearReason);

  const fieldPathToFormKeyMap = useMemo(
    () => buildFieldPathToFormKeyMap(config, isNestedForm),
    [config, isNestedForm],
  );

  const fieldPathToFieldTypeMap = useMemo(() => buildFieldPathToFieldTypeMap(config), [config]);

  const remoteFormValues = useMemo(() => {
    const values = {};

    Object.entries(remoteFieldValues).forEach(([fieldPath, entry]) => {
      if (_.isNil(entry)) {
        return;
      }

      const formKey = fieldPathToFormKeyMap[fieldPath];

      if (!formKey) {
        return;
      }

      values[formKey] = coerceBroadcastValue(entry.value, fieldPathToFieldTypeMap[fieldPath]);
    });

    return values;
  }, [remoteFieldValues, fieldPathToFormKeyMap, fieldPathToFieldTypeMap]);

  const prevRemoteFieldPathsRef = useRef(new Set());

  const preCommitValuesRef = useRef({});

  const fieldPathToFormKeySnapshotRef = useRef({});

  const prevRemoteFormValuesRef = useRef({});

  useEffect(() => {
    const prev = prevRemoteFormValuesRef.current;

    prevRemoteFormValuesRef.current = remoteFormValues;

    if (!setForm || !setInitialFormValues) {
      return;
    }

    const newEntries = Object.entries(remoteFormValues).filter(
      ([key, value]) => prev[key] !== value,
    );

    if (!newEntries.length) {
      return;
    }

    Object.assign(fieldPathToFormKeySnapshotRef.current, fieldPathToFormKeyMap);

    setForm(currentForm => {
      newEntries.forEach(([key]) => {
        if (!(key in preCommitValuesRef.current)) {
          preCommitValuesRef.current[key] = _.get(currentForm, key);
        }
      });

      const updated = _.cloneDeep(currentForm);

      newEntries.forEach(([key, value]) => {
        _.set(updated, key, value);
      });

      return updated;
    });

    setInitialFormValues(currentInitial => {
      const updated = _.cloneDeep(currentInitial);

      newEntries.forEach(([key, value]) => {
        _.set(updated, key, value);
      });

      return updated;
    });
  }, [remoteFormValues, fieldPathToFormKeyMap, setForm, setInitialFormValues]);

  useEffect(() => {
    const currentPaths = new Set(Object.keys(remoteFieldValues));
    const prevPaths = prevRemoteFieldPathsRef.current;

    prevRemoteFieldPathsRef.current = currentPaths;

    if (!setForm || !setInitialFormValues) {
      return;
    }

    // When ALL remote values are cleared at once (currentPaths is empty but prevPaths
    // had entries), this could be a save OR a cancel.
    // - Save (clearReason !== 'cancel'): clear preCommitValues without reverting,
    //   because the saved data is now the committed truth.
    // - Cancel (clearReason === 'cancel'): proceed with revert so that form values
    //   go back to their original pre-edit state.
    const isBulkClear = !currentPaths.size && prevPaths.size;

    if (isBulkClear && clearReason !== 'cancel') {
      preCommitValuesRef.current = {};
      fieldPathToFormKeySnapshotRef.current = {};

      return;
    }

    const revertedFormKeys = [...prevPaths]
      .filter(fp => !currentPaths.has(fp))
      .map(fp => fieldPathToFormKeySnapshotRef.current[fp] || fieldPathToFormKeyMap[fp])
      .filter(Boolean);

    const restoreEntries = revertedFormKeys
      .filter(key => key in preCommitValuesRef.current)
      .map(key => [key, preCommitValuesRef.current[key]]);

    if (!restoreEntries.length) {
      return;
    }

    const applyRestore = prev => {
      const updated = _.cloneDeep(prev);

      restoreEntries.forEach(([key, value]) => {
        _.set(updated, key, value);
      });

      return updated;
    };

    setForm(applyRestore);
    setInitialFormValues(applyRestore);

    restoreEntries.forEach(([key]) => {
      delete preCommitValuesRef.current[key];
    });
    restoreEntries.forEach(([key]) => {
      const snapshotEntry = Object.entries(fieldPathToFormKeySnapshotRef.current).find(
        ([, formKey]) => formKey === key,
      );

      if (snapshotEntry) {
        delete fieldPathToFormKeySnapshotRef.current[snapshotEntry[0]];
      }
    });
  }, [remoteFieldValues, fieldPathToFormKeyMap, setForm, setInitialFormValues, clearReason]);

  useEffect(
    () => () => {
      preCommitValuesRef.current = {};
      prevRemoteFieldPathsRef.current = new Set();
      fieldPathToFormKeySnapshotRef.current = {};
    },
    [],
  );

  const remoteInjectedFormKeys = useMemo(() => {
    const keys = new Set();

    Object.keys(remoteFieldValues).forEach(fieldPath => {
      const formKey = fieldPathToFormKeyMap[fieldPath];

      if (formKey) {
        keys.add(formKey);
      }
    });

    return keys;
  }, [remoteFieldValues, fieldPathToFormKeyMap]);

  const remotePendingChanges = useMemo(
    () =>
      Object.entries(remoteFormValues).reduce((acc, [formKey, remoteValue]) => {
        if (!_.has(preCommitValuesRef.current, formKey)) {
          return acc;
        }

        const originalValue = preCommitValuesRef.current[formKey];

        if (_.isEqual(originalValue, remoteValue)) {
          return acc;
        }

        // Use _.set so that nested-form keys like "[entityId].firstName" produce
        // a nested structure { entityId: { firstName: { newValue, initialValue } } }
        // matching normalizedPendingChanges format. Without this, flat path-based keys
        // cause _.set inside calculatePendingChanges to overwrite entity-level counts
        // with objects, crashing React with "Objects are not valid as a React child".
        _.set(acc, formKey, { newValue: remoteValue, initialValue: originalValue });

        return acc;
      }, {}),
    [remoteFormValues],
  );

  const restoreRemoteInjectedValues = useCallback(form => {
    const remoteKeys = Object.keys(preCommitValuesRef.current);

    if (!remoteKeys.length) {
      return form;
    }

    const cleaned = _.cloneDeep(form);

    remoteKeys.forEach(key => {
      _.set(cleaned, key, preCommitValuesRef.current[key]);
    });

    return cleaned;
  }, []);

  const getCleanFormForSave = useCallback(
    form => {
      const cleaned = restoreRemoteInjectedValues(form);

      preCommitValuesRef.current = {};

      return cleaned;
    },
    [restoreRemoteInjectedValues],
  );

  return {
    remotePendingChanges,
    getCleanFormForSave,
    restoreRemoteInjectedValues,
    remoteInjectedFormKeys,
  };
};

export default useRemoteFieldValueSync;
